import React from 'react';
import teacherService from '../../services/api/teacher';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { getArabicDate } from '../../services/utils/date';
import { getImageUrl } from '../../config/apiConfig';
import { 
  BookOpen, 
  Users, 
  MessageCircle, 
  TrendingUp,
  DollarSign,
  Clock,
  ChevronLeft,
  LayoutDashboard,
  Plus,
  CheckCircle2,
  Sparkles,
  PlayCircle,
  ArrowRight,
  Zap,
  GraduationCap,
  HelpCircle
} from 'lucide-react';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import TeacherStatus from '../../components/teacher/TeacherStatus';
import PendingApprovalMessage from '../../components/teacher/PendingApprovalMessage';

const TeacherDashboard = () => {
  const navigate = useNavigate();
  
  // جلب معلومات المدرس (المنطق الأصلي كاملاً)
  const { data: teacherInfo, isLoading: teacherLoading } = useQuery('teacher-info', () => teacherService.getTeacherInfo(), {
    retry: 1
  });

  // جلب بيانات Dashboard من API واحد فقط (الحل الصح)
  const { data: dashboardData, isLoading: dashboardLoading } = useQuery(
    'teacher-dashboard',
    () => teacherService.getDashboard(),
    {
      retry: 1,
      enabled: teacherInfo?.status === 'approved'
    }
  );

  const isLoading = teacherLoading || (teacherInfo?.status === 'approved' && dashboardLoading);

  // استخدام بيانات Dashboard الموحدة من API واحد
  const stats = dashboardData?.stats || {};
  const recent = dashboardData?.recent || {};

  // تكوين بطاقات الإحصائيات (مع البيانات الموحدة من API واحد)
  const statCards = [
    {
      title: 'مكتبة الكورسات',
      value: stats.total_courses || 0,
      label: 'كورس متاح',
      icon: BookOpen,
      color: 'from-blue-600 to-blue-700',
      shadow: 'shadow-blue-100',
      change: `+${stats.growth?.courses || 0}%`
    },
    {
      title: 'قاعدة الطلاب',
      value: stats.total_students || 0,
      label: 'طالب مشترك',
      icon: Users,
      color: 'from-emerald-600 to-emerald-700',
      shadow: 'shadow-emerald-100',
      change: `+${stats.growth?.students || 0}%`
    },
    {
      title: 'بنك الأسئلة',
      value: stats.total_questions || 0,
      label: 'سؤال طلابي',
      icon: MessageCircle,
      color: 'from-violet-600 to-violet-700',
      shadow: 'shadow-violet-100',
      change: `+${stats.growth?.questions || 0}%`
    },
    {
      title: 'إجمالي الإيرادات',
      value: `${stats.total_revenue || 0}`,
      label: 'جنيه مصري',
      icon: DollarSign,
      color: 'from-amber-500 to-orange-600',
      shadow: 'shadow-orange-100',
      change: `+${stats.growth?.revenue || 0}%`
    }
  ];

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 min-h-[500px]">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-500 font-bold animate-pulse uppercase tracking-widest text-xs">جاري تهيئة لوحة تحكم المعلم...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-10 px-4 sm:px-6 py-6 min-h-screen bg-gray-50/30">
      
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="space-y-1">
          <h1 className="text-3xl font-black text-gray-900 tracking-tight flex items-center gap-3">
            لوحة قيادة المعلم
            <span className="text-sm font-bold bg-primary-50 text-primary-600 px-3 py-1 rounded-full border border-primary-100">
                إدارة المحتوى
            </span>
          </h1>
          <p className="text-gray-500 font-medium flex items-center gap-2">
            <LayoutDashboard size={18} className="text-primary-600" />
            مرحباً بك يا <span className="font-bold text-gray-800">{teacherInfo?.name}</span>، إليك ملخص لأداء طلابك اليوم.
          </p>
        </div>
        <div className="flex items-center gap-4">
            <div className="text-left hidden md:block">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">تاريخ اليوم</p>
                <p className="text-sm font-bold text-gray-700">{getArabicDate(new Date())}</p>
            </div>
            <TeacherStatus status={teacherInfo?.status || 'pending'} />
        </div>
      </div>

      {/* Pending Approval Alert */}
      {teacherInfo?.status === 'pending' && (
          <div className="animate-in fade-in slide-in-from-top duration-700">
            <PendingApprovalMessage />
          </div>
      )}

      {/* Approved Teacher Content */}
      {teacherInfo?.status === 'approved' && (
        <>
          {/* Main Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {statCards.map((card, index) => (
              <div key={index} className={`relative overflow-hidden bg-gradient-to-br ${card.color} rounded-[2rem] p-6 text-white shadow-xl ${card.shadow} transition-transform hover:-translate-y-1 duration-300`}>
                <div className="absolute top-0 left-0 w-24 h-24 bg-white/10 rounded-br-full -z-0"></div>
                <div className="relative z-10">
                  <div className="bg-white/20 w-12 h-12 rounded-xl flex items-center justify-center mb-6 backdrop-blur-md">
                    <card.icon size={24} />
                  </div>
                  <p className="text-white/70 text-[10px] font-black uppercase tracking-widest mb-1">{card.title}</p>
                  <div className="flex items-end gap-2 mb-4">
                      <p className="text-3xl font-black">{card.value}</p>
                      <span className="text-xs font-bold opacity-80 mb-1">{card.label}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-[10px] font-bold bg-black/10 w-fit px-3 py-1 rounded-full">
                    <TrendingUp size={12} />
                    <span>{card.change} من الشهر الماضي</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* Right Side: Courses & Questions (2/3 Width) */}
            <div className="lg:col-span-2 space-y-8">
                
                {/* Courses Preview Section */}
                <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8">
                    <div className="flex items-center justify-between mb-8">
                        <h2 className="text-2xl font-black text-gray-900 flex items-center gap-3">
                            <div className="w-2 h-8 bg-primary-600 rounded-full"></div>
                            أحدث الكورسات المنشورة
                        </h2>
                        <button 
                            onClick={() => navigate('/teacher/courses')}
                            className="text-primary-600 font-bold text-sm flex items-center gap-1 hover:gap-2 transition-all"
                        >
                            عرض الكل <ChevronLeft size={18} />
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {recent.courses?.map((course, index) => (
                        <div key={index} className="group bg-gray-50/50 border border-gray-100 rounded-3xl overflow-hidden hover:bg-white hover:shadow-xl transition-all duration-300">
                            <div className="relative h-40">
                                <img
                                  src={course.thumbnail ? getImageUrl(course.thumbnail) : '/default_course.png'}
                                  alt={course.title}
                                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                  onError={(e) => {
                                    e.target.src = '/default_course.png';
                                  }}
                                  onLoad={(e) => {
                                    // Image loaded successfully
                                  }}
                                />
                                <div className="absolute top-3 left-3">
                                    <span className="bg-white/90 backdrop-blur-md text-emerald-600 text-[10px] font-black px-3 py-1.5 rounded-xl flex items-center gap-1.5 shadow-sm border border-emerald-100">
                                        <CheckCircle2 size={12} /> نشط
                                    </span>
                                </div>
                            </div>
                            <div className="p-5">
                                <h3 className="font-black text-gray-900 text-lg mb-4 line-clamp-1 group-hover:text-primary-600 transition-colors">{course.title}</h3>
                                <div className="flex items-center justify-between p-3 bg-white rounded-2xl border border-gray-100">
                                    <div className="flex items-center gap-2">
                                        <Users size={16} className="text-blue-500" />
                                        <span className="text-xs font-black text-gray-700">{course.student_count || 0} طالب</span>
                                    </div>
                                    <div className="w-[1px] h-4 bg-gray-100"></div>
                                    <div className="flex items-center gap-2">
                                        <PlayCircle size={16} className="text-emerald-500" />
                                        <span className="text-xs font-black text-gray-700">{course.lesson_count || 0} درس</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        ))}
                    </div>
                </div>

                {/* Questions Section */}
                <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8">
                    <div className="flex items-center justify-between mb-8">
                        <h2 className="text-2xl font-black text-gray-900 flex items-center gap-3">
                            <div className="w-2 h-8 bg-orange-500 rounded-full"></div>
                            آخر استفسارات الطلاب
                        </h2>
                        <button 
                            onClick={() => navigate('/teacher/questions')}
                            className="text-orange-600 font-bold text-sm flex items-center gap-1 hover:gap-2 transition-all"
                        >
                            إدارة كافة الأسئلة <ChevronLeft size={18} />
                        </button>
                    </div>

                    <div className="space-y-4">
                        {recent.questions?.length > 0 ? (
                            recent.questions.map((question, index) => (
                            <div key={index} className="flex items-center gap-5 p-5 bg-gray-50/50 rounded-3xl border border-gray-100 group/q hover:bg-white hover:shadow-lg transition-all duration-300">
                                <div className="w-12 h-12 rounded-2xl bg-blue-100 text-blue-600 flex items-center justify-center font-black flex-shrink-0 group-hover/q:scale-110 transition-transform">
                                    {question.student_name?.[0]}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between mb-1">
                                        <p className="text-sm font-black text-gray-900">{question.student_name}</p>
                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">{question.time}</span>
                                    </div>
                                    <p className="text-xs text-gray-500 line-clamp-1 mb-2 font-medium">{question.text}</p>
                                    <div className="flex items-center gap-2">
                                        <span className="text-[10px] font-black text-primary-500 uppercase tracking-widest">{question.course_title}</span>
                                        {question.answered ? (
                                            <span className="inline-flex items-center gap-1 text-[9px] font-black text-emerald-600 uppercase"><CheckCircle2 size={10}/> تمت الإجابة</span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 text-[9px] font-black text-orange-500 uppercase"><Clock size={10}/> بانتظار الرد</span>
                                        )}
                                    </div>
                                </div>
                                {!question.answered && (
                                    <button 
                                        onClick={() => navigate('/teacher/questions', { state: { questionId: question.id } })}
                                        className="bg-primary-600 text-white px-5 py-2 rounded-xl font-black text-xs hover:bg-primary-700 transition-all shadow-lg shadow-primary-100 transform active:scale-95"
                                    >
                                        رد الآن
                                    </button>
                                )}
                            </div>
                            ))
                        ) : (
                            <div className="py-12 text-center">
                                <MessageCircle size={48} className="mx-auto text-gray-200 mb-4" />
                                <p className="text-gray-400 font-bold uppercase tracking-widest text-xs">لا توجد أسئلة جديدة بانتظارك حالياً</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Left Side: Sidebar Actions & Info (1/3 Width) */}
            <div className="space-y-8">
                
                {/* Quick Actions Hub */}
                <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8">
                    <h3 className="text-xl font-black text-gray-900 mb-8 flex items-center gap-3">
                        <Zap className="text-primary-600" />
                        إجراءات إدارية سريعة
                    </h3>
                    <div className="space-y-4">
                        <button 
                            onClick={() => navigate('/teacher/create-course')}
                            className="w-full p-6 bg-blue-50 border border-blue-100 rounded-3xl hover:bg-blue-600 hover:text-white transition-all group flex flex-col items-end text-right"
                        >
                            <div className="bg-white p-3 rounded-2xl text-blue-600 mb-4 group-hover:scale-110 transition-transform">
                                <Plus size={24} />
                            </div>
                            <p className="font-black text-gray-900 group-hover:text-white mb-1">تجهيز كورس جديد</p>
                            <p className="text-[10px] text-gray-400 group-hover:text-white/80 font-bold uppercase tracking-widest">إضافة دروس ومرفقات</p>
                        </button>

                        <button 
                            onClick={() => navigate('/teacher/questions', { state: { filter: 'unanswered' } })}
                            className="w-full p-6 bg-emerald-50 border border-emerald-100 rounded-3xl hover:bg-emerald-600 hover:text-white transition-all group flex flex-col items-end text-right"
                        >
                            <div className="bg-white p-3 rounded-2xl text-emerald-600 mb-4 group-hover:scale-110 transition-transform">
                                <HelpCircle size={24} />
                            </div>
                            <p className="font-black text-gray-900 group-hover:text-white mb-1">مركز الردود</p>
                            <p className="text-[10px] text-gray-400 group-hover:text-white/80 font-bold uppercase tracking-widest">{stats.pending_questions || 0} استفسارات بانتظارك</p>
                        </button>

                        <button className="w-full p-6 bg-purple-50 border border-purple-100 rounded-3xl hover:bg-purple-600 hover:text-white transition-all group flex flex-col items-end text-right">
                            <div className="bg-white p-3 rounded-2xl text-purple-600 mb-4 group-hover:scale-110 transition-transform">
                                <TrendingUp size={24} />
                            </div>
                            <p className="font-black text-gray-900 group-hover:text-white mb-1">تحليلات الأداء</p>
                            <p className="text-[10px] text-gray-400 group-hover:text-white/80 font-bold uppercase tracking-widest">تتبع تفاعل الطلاب</p>
                        </button>
                    </div>
                </div>

                {/* Motivational Achievement Card */}
                <div className="bg-gray-900 rounded-[2.5rem] p-10 text-white relative overflow-hidden group shadow-2xl">
                    <div className="absolute -top-10 -right-10 w-40 h-40 bg-primary-600/20 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700"></div>
                    <div className="relative z-10">
                        <div className="bg-primary-600 w-12 h-12 rounded-2xl flex items-center justify-center mb-8 shadow-xl">
                            <Sparkles size={28} />
                        </div>
                        <h3 className="text-2xl font-black mb-4 leading-tight tracking-tight">طوّر محتواك <br /> لزيادة مبيعاتك!</h3>
                        <p className="text-gray-400 text-sm font-medium leading-relaxed mb-10">
                            تفيد إحصائياتنا بأن المدرسين الذين يرفعون فيديوهات تعريفية جذابة تزيد نسبة اشتراك طلابهم بمعدل 60%.
                        </p>
                        <button 
                            onClick={() => navigate('/profile')}
                            className="w-full bg-white text-gray-900 font-black py-4 rounded-2xl hover:bg-primary-50 transition-all flex items-center justify-center gap-2 transform active:scale-95 shadow-xl"
                        >
                            رفع فيديو تعريفي <ArrowRight size={20} className="rotate-180" />
                        </button>
                    </div>
                </div>
            </div>

          </div>
        </>
      )}

      {/* Pending Status View (Logic Preserved) */}
      {teacherInfo?.status === 'pending' && (
        <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 lg:p-12 text-center animate-in fade-in duration-700 max-w-4xl mx-auto">
            <div className="w-24 h-24 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-8 shadow-inner border border-amber-100">
                <Clock className="text-amber-500 animate-pulse" size={48} />
            </div>
            <h2 className="text-3xl font-black text-gray-900 mb-4 tracking-tight">ملفك الشخصي قيد المراجعة</h2>
            <p className="text-gray-500 text-lg leading-relaxed mb-10 max-w-2xl mx-auto font-medium">
                بمجرد موافقة مدير المنصة، ستتمكن من البدء في رفع الكورسات التعليمية وإضافة الطلاب. شكراً لصبرك.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-right">
                <div className="p-6 rounded-[2rem] border border-gray-100 bg-gray-50/50 hover:bg-white transition-all group">
                    <div className="flex items-center justify-between mb-4">
                        <div className="bg-white p-3 rounded-2xl text-blue-600 shadow-sm group-hover:scale-110 transition-transform">
                            <User2Icon size={24} />
                        </div>
                        <button 
                            onClick={() => navigate('/profile')}
                            className="bg-primary-600 text-white px-5 py-2 rounded-xl font-black text-xs hover:bg-primary-700 transition-all"
                        >
                            تعديل
                        </button>
                    </div>
                    <h4 className="font-black text-gray-900 mb-1 leading-none">تحديث المعلومات الشخصية</h4>
                    <p className="text-xs text-gray-400 font-bold uppercase tracking-tighter">الاسم، النبذة، والمدينة</p>
                </div>

                <div className="p-6 rounded-[2rem] border border-gray-100 bg-gray-50/50 hover:bg-white transition-all group">
                    <div className="flex items-center justify-between mb-4">
                        <div className="bg-white p-3 rounded-2xl text-emerald-600 shadow-sm group-hover:scale-110 transition-transform">
                            <GraduationCap size={24} />
                        </div>
                        <button 
                            onClick={() => navigate('/profile')}
                            className="bg-emerald-600 text-white px-5 py-2 rounded-xl font-black text-xs hover:bg-emerald-700 transition-all"
                        >
                            عرض
                        </button>
                    </div>
                    <h4 className="font-black text-gray-900 mb-1 leading-none">المواد والصفوف المختارة</h4>
                    <p className="text-xs text-gray-400 font-bold uppercase tracking-tighter">التخصصات الحالية</p>
                </div>
            </div>
        </div>
      )}
    </div>
  );
};

// أيقونات إضافية
const User2Icon = ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
    </svg>
);

export default TeacherDashboard;